import React from 'react';
import { useCart } from "./contexts/CartContext";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import { Container } from "@chakra-ui/react";
import { Input } from "@chakra-ui/react";
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Button,
  Image,
  VStack,
  HStack,
  useDisclosure,
  Wrap,
  Flex,
} from '@chakra-ui/react';

import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from "@chakra-ui/react";

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "@chakra-ui/react";

type MenuItem = {
  id: number;
  name: string;
  description: string;
  category: string;
  price: number;
  image: string;
};

export default function App() {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [clientType, setClientType] = useState<"hotel" | "restaurant" | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const [clientName, setClientName] = useState<string>("");
  const [clientLogo, setClientLogo] = useState<string>("");
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { addItem, items, total, clearCart, increaseQuantity, decreaseQuantity } = useCart();
  const [orderId, setOrderId] = useState<number | null>(null);
  const [orderTotal, setOrderTotal] = useState<number>(0);
  const [customerName, setCustomerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [branding, setBranding] = useState<any>({
    primary: "#075B5E",
    secondary: "#F4631E",
    background: "#309898",
    tile: "#FFFFFF",
    support: "#333333",
    logo: "",  // fallback
    discount_percentage: null,
    google_review_link: "",
    instagram_link: "",
    facebook_link: ""
  });
  const categories = Array.from(new Set(
    Array.isArray(menuItems) ? menuItems.map((item) => item.category) : []
  ));
  //const subdomain = window.location.hostname.split('.')[0]; // e.g., 'aciddrop'
  const subdomain = process.env.REACT_APP_FAKE_SUBDOMAIN || window.location.hostname.split('.')[0];
  const filteredMenuItems = selectedCategory
  ? menuItems.filter((item) => item.category === selectedCategory)
  : menuItems;

  // One disclosure for Cart Drawer
  const {
    isOpen: isCartOpen,
    onOpen: onCartOpen,
    onClose: onCartClose,
  } = useDisclosure(); 


  const [roomOrTable, setRoomOrTable] = useState("");
  console.log("👀 REACT_APP_BACKEND_URL =", backendUrl);


  // Separate disclosure for Order Confirmation Modal
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();

  const handleCheckout = () => {
    if (items.length === 0) {
      alert("Your cart is empty!");
      return;
    }
    if (!roomOrTable || !customerName) {
      alert("Please fill in both room/table number and your name.");
      return;
    }

    fetch(`${backendUrl}/order`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        client_id: clientId,
        room_or_table: roomOrTable,
        customer_name: customerName,
        phone_number: phoneNumber,
        items: items.map(i => ({
          id: i.id,
          name: i.name,
          price: i.price,
          quantity: i.quantity
        })),
        total: total
      }),
    })
      .then(res => res.json())
      .then(data => {
        setOrderId(data.order_id);
        setOrderTotal(total);
        clearCart();
        onCartClose();
        onModalOpen();  // Open confirmation modal
      })
      .catch(err => console.error(err));
  };

  useEffect(() => {
    // If we already have a clientId, no need to refetch
    if (clientId !== null) return;
    const fetchClient = async () => {
      try {
        if (subdomain && subdomain !== "www") {
          const res = await fetch(`${backendUrl}/client/by-subdomain/${subdomain}`);
          if (!res.ok) throw new Error("Client not found for subdomain");
          const data = await res.json();
          console.log("👀 Subdomain-based client fetch response:", data);
          setClientType(data.type);
          setClientId(data.id);
          setClientName(data.name); // 👈 Set name
          setClientLogo(data.logo_url); // 👈 Set logo
        } else {
          // No subdomain found – fallback to default client
          console.log("⚠️ No subdomain detected – falling back to default client (id: 999)");
          setClientId(999);
          setClientType("restaurant"); // or default fallback
        }
      } catch (err) {
        console.error("❌ Client fetch error, using default:", err);
        setClientId(999);
        setClientType("restaurant"); // fallback type
      }
    };

    fetchClient();
    if (!subdomain || !backendUrl) return;
    fetch(`${backendUrl}/branding/by-subdomain/${subdomain}`)
      .then(res => res.json())
      .then(data => {
        console.log("🎨 Branding loaded:", data);
        setBranding(data);
      })
      .catch(err => {
        console.error("Branding fetch error", err);
      });
  }, [backendUrl, subdomain, clientId]);

  useEffect(() => {
    if (!clientId) return;

    fetch(`${backendUrl}/menu/${clientId}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch menu");
        return res.json();
      })
      .then((data) => {
        if (!Array.isArray(data)) throw new Error("Menu response is not an array");
        setMenuItems(data);
      })
      .catch((err) => {
        console.error("MENU FETCH ERROR:", err);
        setMenuItems([]); // fallback to empty array to avoid crash
      });
  }, [backendUrl, clientId]);

  if (!clientId || !clientType) {
    return (
      <Container centerContent p={10}>
        <Text color={branding.support}>Loading menu...</Text>
      </Container>
    );
  }

  return (
    
    <Box p={4} bg={branding.background} minH="100vh">
    <Helmet>
      <title>{clientName ? `${clientName} Menu` : "Blue Monk Eats"}</title>
      <meta name="description" content={`Explore the menu from ${clientName || "our partner restaurant"}`} />
    </Helmet>
    <Container maxW="container.lg" p={4} boxShadow="lg">
    <Box p={4} bg={branding.background} minH="100vh">
      {/* Header with dynamic logo and name */}
      <Flex justify="center" align="center" mb={6}>
        {clientLogo && (
          <Image
            src={clientLogo || "/default-logo.png"}
            alt={`${clientName} logo`}
            h="40px"
            mr={3}
            borderRadius="md"
          />
        )}
        <Heading 
          as="h1" 
          size={{ base: "md", md: "lg" }}
          bg={branding.tile}
          color={branding.primary}
          p={4}
          borderRadius="md"
          textAlign="center"
        >
          {clientName ? `${clientName} Menu` : "Browse Our Menu"} 🍽️
        </Heading>
      </Flex>

      {/* Category Buttons */}
      <Wrap spacing={2} mb={4} justify="center">
        <Button
          variant="solid"
          bg={selectedCategory === null ? branding.primary : branding.tile}
          color={selectedCategory === null ? branding.background : branding.support}
          border={`2px solid ${branding.primary}`}
          onClick={() => setSelectedCategory(null)}
        >
          All
        </Button>
        {categories.map((category) => (
          <Button
            key={category}
            variant="solid"
            //colorScheme="yellow"
            bg={selectedCategory === category ? branding.primary : branding.tile}
            color={selectedCategory === category ? branding.background : branding.support}
            border={`2px solid ${branding.primary}`}
            _hover={{ bg: branding.secondary }}
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </Button>
        ))}
      </Wrap>

      {/* Menu Items */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        {filteredMenuItems.map((item) => (
          <Box
            key={item.id}
            p={4}
            bg={branding.tile}
            boxShadow="base"
            //borderWidth="1px"
            borderRadius="xl"
          >
            <VStack spacing={3}>
              <Image
                src={item.image}
                alt={item.name}
                borderRadius="md"
                objectFit="cover"
                w="100%"
                h={{ base: "180px", md: "200px" }}
              />
              <Box w="100%">
                <Heading size="md" color={branding.support}>{item.name}</Heading>
                <Text fontSize="sm" color={branding.support} mb={2}>
                  {item.description}
                </Text>
                <Text fontWeight="bold" color={branding.support}>₹{item.price}</Text>
                <HStack mt={2}>
                  <Button
                    size="xs"
                    onClick={() => decreaseQuantity(item.id)}
                    isDisabled={!items.find((i) => i.id === item.id)}
                  >
                    <MinusIcon />
                  </Button>
                  <Text>{items.find((i) => i.id === item.id)?.quantity || 0}</Text>
                  <Button
                    size="xs"
                    aria-label="Add item"
                    onClick={() =>
                      addItem({
                        id: item.id,
                        name: item.name,
                        price: item.price,
                        quantity: 1,
                      })
                    }
                  >
                    <AddIcon />
                  </Button>
                </HStack>
              </Box>
            </VStack>
          </Box>
        ))}
      </SimpleGrid>

      {/* 🛒 Floating Cart Button and Drawer */}
      <Button
        bg={branding.primary}
        color={branding.background}
        _hover={{ bg: "brand.secondary" }}
        position="fixed"
        bottom="20px"
        right="20px"
        boxShadow="md"
        onClick={onCartOpen}
      >
        🛒 View Cart ({items.length})
      </Button>

      {/* Drawer for Cart */}
      <Drawer isOpen={isCartOpen} placement="right" onClose={onCartClose}>
        <DrawerOverlay />
        <DrawerContent bg={branding.support}>
          <DrawerCloseButton />
          <DrawerHeader color={branding.background}>Your Cart</DrawerHeader>

          <DrawerBody>
            {items.length === 0 ? (
              <Text>Your cart is empty.</Text>
            ) : (
              items.map((item) => (
                <Box key={item.id} mb={4}>
                  <Text fontWeight="medium" color={branding.background}> {item.name}</Text>
                  <HStack justify="space-between">
                    <HStack>
                      <Button size="xs" onClick={() => decreaseQuantity(item.id)}><MinusIcon /></Button>
                      <Text color={branding.background}>{item.quantity}</Text>
                      <Button size="xs" onClick={() => increaseQuantity(item.id)}><AddIcon /></Button>
                    </HStack>
                    <Text color={branding.background}>₹{item.price * item.quantity}</Text>
                  </HStack>
                </Box>
              ))
            )}
          </DrawerBody>

          <DrawerFooter>
            <VStack w="100%" spacing={3}>
            <Input
                placeholder={clientType === "hotel" ? "Room Number" : "Table Number"}
                value={roomOrTable}
                onChange={(e) => setRoomOrTable(e.target.value)}
                isRequired
                bg="white"
                autoFocus={!roomOrTable}
              />
            <Input
                placeholder="Your Name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                isRequired
                bg="white"
              />
              <Input
                placeholder="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                type="tel"
                bg="white"
              />
              <Text fontWeight="bold">Total: ₹{total}</Text>
              <Button colorScheme="green" w="100%" onClick={handleCheckout}>
                Checkout
              </Button>
              
              <Button variant="outline" w="100%" onClick={clearCart}>
                Clear Cart
              </Button>
            </VStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Order Confirmation Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose} isCentered>
        <ModalOverlay />
        <ModalContent bg={branding.tile} borderRadius="lg" p={4}>
          <ModalHeader textAlign="center" fontSize="xl" color={branding.primary}>Order Confirmed ✅</ModalHeader>
          <ModalBody>
          <VStack spacing={4} textAlign="center">
            <Text color={branding.support}>Thank you for your order!</Text>
            <Text fontWeight="medium" color={branding.support}>
              Your Order ID is <strong>#{orderId}</strong>
            </Text>
            <Text color={branding.support}>Total Amount: ₹{orderTotal}</Text>
            <Text fontSize="sm" color={branding.support}>
              Please pay at the counter.
            </Text>

            {/* Conditional rendering for review promo */}
            {branding.google_review_link && branding.instagram_link && branding.discount_percentage != null && (
              <Box
                bg={branding.support}
                p={3}
                borderRadius="md"
                mt={4}
                fontSize="sm"
                color="gray.700"
              >
                <Text mb={2}>
                  ⭐ Love your meal? Leave us a{" "}
                  <a
                    href={branding.google_review_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: "bold" }}
                  >
                    Google review
                  </a>{" "}
                  and tag us on{" "}
                  <a
                    href={branding.instagram_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: "bold" }}
                  >
                    Instagram
                  </a>
                  !
                </Text>
                <Text fontWeight="bold">
                  Show it at the counter to get{" "}
                  <span>
                    {branding.discount_percentage}% off
                  </span>{" "}
                  your bill 🎉
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>
          <ModalFooter>
            <Button bg="brand.primary" color="white" onClick={onModalClose} _hover={{ bg: "brand.secondary" }}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
    </Container>
    <Box as="footer" textAlign="center" mt={10} py={4} fontSize="sm" color="gray.600">
      © {new Date().getFullYear()} Blue Monk Eats. All rights reserved. <br />
      Powered by{" "}
      <a href="https://www.bluemonkai.com" target="_blank" rel="noopener noreferrer" style={{ color: "#075B5E", fontWeight: "bold" }}>
        Blue Monk AI
      </a>
    </Box>
    
    </Box>
  );
}
